/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import util from "../util/util.js";

import validate from "../util/validate.js";

import birdata from "../api.js";
import MediaUploaderViewModel from "../ui/shared/media-uploader.js";

/**
 * The grand idea here is to reduce boilerplate to implement fields and validation
 *
 * Fields can be defined using a simple JSON structure, e.g.:
 * { name: "firstName", type: "text", validate: "required" }
 *
 * The idea is to avoid the boring majority of code around defining fields, while still providing full flexibility to do custom things
 * with fields. Every new feature added to this class needs to be carefully weighed in terms of how much boilerplate it removes vs how
 * much complexity it adds to this code. It's important that this code remains easily understood since it is going to be used very widely.
 */

function optionForCode(options, code, lookupKey) {
	return ko.unwrap(options).find(o => o[lookupKey] == code);
}

function isBlank(x) {
	return typeof x === "string" && x.trim() == "";
}

function isNullish(x) {
	return x === null || x === undefined;
}

function field(config) {
	config = config || {};

	// Special case - convert to an options field
	if(config.type === 'species') {
		config.type = 'options';
		config.options = ko.observable([]);
		birdata.speciesList().then(config.options);
		config.initPromise = birdata.speciesList();
		config.optionsText = 'common_name';
		config.lookupKey = 'id';
		config.searchable = true;
		config.key = config.key || camelToSnake(config.name) + "_id";
	}

	var isArrayType = (config.type === "options" && config.multiselect) ||
		(config.type === "compound" && config.maxCount > 1) ||
		(config.type === "multi-text") ||
		(config.type === "media") ||
		config.isArrayType;

	var value = config.value || (isArrayType ? ko.observableArray() : ko.observable());
	if('defaultValue' in config) {
		value(config.defaultValue);
	} else if(config.type === "boolean" && !config.allowNull) {
		value(false);
	}

	var name = config.name;

	var required = config.required;

	let validators = ([config.validate || []])
		.flat(Number.MAX_SAFE_INTEGER)
		.map(v => {
			if(typeof v === 'string') {
				if(v.split(",").includes("required")) {
					required = true;
				}
				return parseValidators(v);
			} else {
				return v;
			}
		})
		.flat(Number.MAX_SAFE_INTEGER);

	var self = {
		name: name,

		value: value,

		formattedValue: function() {
			if(config.formattedValue) {
				return config.formattedValue(value());
			}

			let val = value();

			if(isNullish(val) || isBlank(val)) {
				if(config.blankIsIndeterminate) {
					return "Present";
				} else {
					return "";
				}
			}

			if(config.type === 'options') {
				if(config.multiselect) {
					return val.map(v => v[self.optionsText]).join(", ");
				} else {
					return val[self.optionsText];
				}
			}

			if(config.type === 'number' || config.type === 'integer') {
				if(val === "") {
					return "";
				} else {
					return util.formatNumber(+val) + (config.units ? " " + config.units : "");
				}
			}

			if(config.type === 'boolean' || config.type === 'checkbox') {
				return val ? 'Yes' : 'No';
			}

			if(config.type === 'multi-text') {
				return val.map(v => v.value()).join("; ")
			}

			if(config.type === 'media') {
				return util.formatQuantity(val.length, "item");
			}

			return val;
		},

		formattedValueHtml: function() {
			if(config.type === 'media') {
				return value().map(item =>
					`<a href="${item.url()}" target="_blank">
						<img src="${item.thumbUrl}" class="attachment-thumbnail" alt="Attachment thumbnail">
					</a>`
				).join(' ');
			}

			return util.escapeHtml(this.formattedValue());
		},

		type: config.type,

		help: config.help,

		key: config.key || camelToSnake(name),

		label: config.label || camelToLabel(name),

		units: config.units, // Units for numeric fields
		multiselect: config.multiselect,
		multiline: config.multiline,
		allowNull: config.allowNull,
		max: config.max,
		min: config.min,
		maxLength: config.maxLength,
		decimalPlaces: config.decimalPlaces,
		placeholder: config.placeholder,
		searchable: config.searchable,
		withDescendants: function(visibleOnly) {
			if(visibleOnly && this.hidden()) {
				return [];
			}
			var result = [this];
			if(config.type === 'compound') {
				let val = value();
				let values = config.maxCount == 1 ? (val ? [val] : []) : val;

				// This would be nicer with flatMap
				values.forEach(v =>
					v._fields.forEach(f =>
						f.field.withDescendants().forEach(f2 =>
							result.push(f2))));
			}
			return result;
		},
		to_json: function() {
			if(config.to_json) {
				return config.to_json(value);
			}

			if(config.hidden && config.hidden()) {
				return undefined;
			}

			let val = value();

			if(config.type === 'number' || config.type === 'integer') {
				if(isBlank(val)) {
					return null;
				}
				return (val === undefined || val === null) ? undefined : +val;
			}

			if(config.type === 'options') {
				if(config.multiselect) {
					return val && val.map(v => v[this.lookupKey]);
				} else {
					return val && val[this.lookupKey];
				}
			}

			if(config.type === 'compound') {
				var item_to_json = function(item) {
					var x = {};
					for (const [key, f] of Object.entries(item)) {
						if(key != '_fields' && f.field) {
							f.field.save(x);
						}
					}
					return x;
				};

				return (config.maxCount == 1) ? (val && item_to_json(val)) : val.map(item_to_json);
			}

			if(config.type === 'multi-text') {
				return val
					.map(v => v.value())
					.filter(v => v && v.trim && v.trim());
			}

			if(config.type === 'media') {
				return val.map(x => x.uuid()).filter(x => x);
			}

			if(config.type === 'local-time') {
				// Zero pad hours if needed
				if(val && val.match(/^[0-9]:[0-9]{2}$/)) {
					val = '0' + val;
				}
			}

			return val;
		},

		from_json: function(val) {
			if(config.from_json) {
				return value(config.from_json(val));
			}

			if(config.type === 'options') {
				if(config.multiselect) {
					value((val ?? []).map(code => optionForCode(config.options, code, self.lookupKey)));
				} else {
					value(optionForCode(config.options, val, self.lookupKey));
				}
			} else if(config.type === 'compound') {
				value((config.maxCount == 1) ? (val && self.createItem(val)) : val.map(self.createItem));
			} else if(config.type === 'multi-text') {
				value(val.map(v => ({ value: ko.observable(v) })));
			} else if(config.type === 'media') {
				let uuids = val ?? []
				value(uuids.map(uuid => MediaUploaderViewModel.createMediaModel(null, uuid)));
			} else {
				value(val);
			}
		},

		load: config.load || function(json) {
			// This is required because sometimes we have to wait for options etc. to load first
			if(config.initPromise) {
				config.initPromise.then(function() {
					self.from_json(json[self.key]);
				});
			}
			this.from_json(json[this.key]);
		},

		save: config.save || function(json) {
			json[this.key] = this.to_json();
		},

		validate: function() {
			if(config.hidden && config.hidden()) {
				return undefined;
			}
			for(var i = 0; i < validators.length; i++) {
				var v = validators[i];
				var result = v(self);
				if(result) return result;
			}
		},

		required: required,

		options: config.options,
		optionsText: config.optionsText || 'name',
		lookupKey: config.lookupKey || 'code',
		sortOptions: config.sortOptions ?? true,

		hidden: config.hidden || function() { return false; },
		style: config.style,
		uuid: util.uuid(),
		disabled: config.disabled,

		// For boolean yes/no/unknown
		optionLabels: config.optionLabels,

		optionImages: config.optionImages,
		imageForOption: function(option) {
			if(config.optionImages && option) {
				let image = config.optionImages[option.code];
				if(image) {
					return birdata.APP_ROOT_URL + "images/" + image;
				}
			}
		},

		userData: config.userData
	};

	if(config.type === 'compound') {
		var createItem = function(json /* optional */) {
			var fields = config.fields.map(field);
			var x = Object.fromEntries(fields.map(function(f) {
				if(json) {
					f.field.load(json);
				}
				return [f.field.name, f];
			}));
			x._fields = fields;

			x.removeItem = function(item) {
				value.splice(value.indexOf(item), 1);
			};

			return x;
		};
		self.createItem = createItem;

		if(config.maxCount > 1) {
			self.addNewItem = function() {
				value.push(createItem());
			}
			for(var i = 0; i < config.minCount; i++) {
				self.addNewItem();
			}
		}
		if(config.maxCount == 1) {
			self.exists = ko.pureComputed({
				read: function() { return !!value(); },
				write: function(x) {
					if(x && !value()) {
						value(createItem());
					}
					if(!x && !!value()) {
						value(null);
					}
				}
			});
			if(config.minCount > 0) {
				self.exists(true);
			}
		}

		self.canAddItem = ko.pureComputed(function() {
			if(config.maxCount == 1) {
				return !self.exists();
			} else {
				return value().length < config.maxCount;
			}
		});

		self.canRemoveItem = ko.pureComputed(function() {
			if(config.maxCount == 1) {
				return self.exists();
			} else {
				return value().length > config.minCount;
			}
		});
	}

	if(config.type === 'multi-text') {
		self.addItem = function() {
			self.focusLastItem = true; // Auto focus newly added item (see self.itemHasFocus)
			value.push({ value: ko.observable("") })
		}

		self.removeItem = function(item) {
			value.splice(value.indexOf(item), 1)
		}

		self.handleKeyDown = function(item, evt) {
			if(evt.keyCode === 13) { // ENTER
				self.addItem()
			} else {
				return true;
			}
		}

		self.itemHasFocus = function(item) {
			if(self.focusLastItem && value.indexOf(item) === value().length - 1) {
				self.focusLastItem = false;
				return true;
			} else {
				return false;
			}
		}

		self.addLabel = config.addLabel || 'Add';
		self.removeLabel = config.removeLabel || 'Remove';
	}

	if(config.type == 'text' && config.options) {
		self.otherValue = ko.observable('');
		self.optionValue = ko.observable();
		self.isOther = ko.pureComputed(function() {
			return self.optionValue() == (config.otherOption || 'Other');
		});
		self.otherValue.subscribe(function(x) {
			if(self.isOther()) {
				self.value(x);
			}
		});
		self.optionValue.subscribe(function(x) {
			if(!self.isOther()) {
				self.value(x);
			} else {
				if(self.otherValue() || self.value()) {
					self.value(self.otherValue());
				}
			}
		});
		self.value.subscribe(function(x) {
			if(!(self.isOther() && self.otherValue() == x)) {
				if(ko.unwrap(self.options).indexOf(x) == -1) {
					self.otherValue(x);
					self.optionValue('Other');
				} else {
					self.optionValue(x);
				}
			}
		});
		self.handleChangeEvent = function(data, event) {
			setTimeout(function() {
				if(self.isOther()) {
					event.target.nextElementSibling.focus()
				}
			});
		}
	}

	// Enable standard validation functionality
	validate(self.value, self.validate);
	if(config.tab) {
		value.validateInfo = { tab: config.tab };
	}

	// Critical point: this makes it possible to access this field object from observable (obs.field)
	self.value.field = self;

	return self.value;
}

function camelToWords(str) {
	var i, m = 0, wordStart = 0, wordEnd, words = [], upper, lastUpper;

	for(i = 0; i < str.length; i++) {
		upper = (str[i] == str[i].toUpperCase());
		if(upper == lastUpper) continue;

		wordEnd = upper ? i : i - 1;

		if(wordEnd > wordStart) {
			words.push(str.substring(wordStart, wordEnd));
			wordStart = wordEnd;
		}
		lastUpper = upper;
	}
	words.push(str.substring(wordStart));

	return words;
}

function camelToSnake(str) {
	return camelToWords(str).join("_").toLowerCase();
}

function camelToLabel(str) {
	str = camelToWords(str).join(" ").toLowerCase();
	if(str.length > 0) {
		str = str[0].toUpperCase() + str.substring(1);
	}
	return str;
}

/** Validators **/

function parseValidators(str) {
	return str.split(",").map(function(s) {
		if(s === 'required') {
			return required();
		}

		var m = s.match(/([<>]=?)(-?[0-9.]+)/);
		if(m) {
			let operator = m[1];
			let operand = +m[2];
			switch(operator) {
				case '>': return moreThan(operand);
				case '<': return lessThan(operand);
				case '>=': return moreThanOrEq(operand);
				case '<=': return lessThanOrEq(operand);
			}
		}

		throw Error('Invalid validator: ' + s);
	});
}

function required(msg) {
	return function(f) {
		if(f.value() === null || f.value() === undefined || f.value() === "") {
			return msg || f.label + " is required";
		}
	};
}

function integer(msg) {
	return function(f) {
		if(f.value() && !util.isInt(f.value())) {
			return msg || f.label + " must be a whole number";
		}
	};
}

function moreThan(n, msg) {
	return function(f) {
		if((f.value() || f.value() === 0) && f.value() <= n) {
			return msg || f.label + " must be greater than " + n + (f.units ? " " + f.units : "");
		}
	};
}

function moreThanOrEq(n, msg) {
	return function(f) {
		if((f.value() || f.value() === 0) && f.value() < n) {
			return msg || f.label + " must be greater than or equal to " + n + (f.units ? " " + f.units : "");
		}
	};
}

function lessThan(n, msg) {
	return function(f) {
		if((f.value() || f.value() === 0) && f.value() >= n) {
			return msg || f.label + " must be less than " + n + (f.units ? " " + f.units : "");
		}
	};
}

function lessThanOrEq(n, msg) {
	return function(f) {
		if((f.value() || f.value() === 0) && f.value() > n) {
			return msg || f.label + " must be less than or equal to " + n + (f.units ? " " + f.units : "");
		}
	};
}

export default {
	field: field
};
